import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles, Upload, FileText, Film, Music, Mic, Palette, Camera, Cpu, Play, Star, Lightbulb, TrendingUp, Eye } from "lucide-react";
import { celebrate } from "@/components/Celebrate";

const ICONS = {
  animation: Film, filmmaking: Film, story_writing: FileText,
  music: Music, acting: Mic, photography: Camera, game_design: Cpu, podcasting: Mic,
};

const VERDICT_STYLE = {
  "Strongly Pursue": { bg: "#DCFCE7", color: "#166534", icon: Star },
  "Pursue with Growth": { bg: "#FEF3C7", color: "#92400E", icon: TrendingUp },
  "Explore More First": { bg: "#DBEAFE", color: "#1E40AF", icon: Lightbulb },
  "Consider Alternatives": { bg: "#FEE2E2", color: "#991B1B", icon: Lightbulb },
};

export default function CreativeStudio() {
  const [fields, setFields] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [fieldId, setFieldId] = useState("");
  const [title, setTitle] = useState("");
  const [writeup, setWriteup] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    const [f, s] = await Promise.all([api.get("/creative/fields"), api.get("/creative/submissions")]);
    setFields(f.data);
    setSubmissions(s.data);
    if (!fieldId && f.data[0]) setFieldId(f.data[0].id);
  };

  useEffect(() => { load().finally(() => setLoading(false)); }, []); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!fieldId || !writeup.trim()) { toast.error("Pick a field and add a description"); return; }
    setBusy(true);
    try {
      let res;
      if (file) {
        const fd = new FormData();
        fd.append("field_id", fieldId);
        fd.append("writeup", writeup);
        fd.append("title", title);
        fd.append("file", file);
        res = await api.post("/creative/submit-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        res = await api.post("/creative/submit-text", { field_id: fieldId, writeup, title });
      }
      toast.success("AI critique ready!");
      celebrate("AI critique ready!");
      setSelected(res.data.submission);
      setShowForm(false);
      setTitle(""); setWriteup(""); setFile(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  if (loading) return <AppShell><div className="p-10 text-slate-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading studio…</div></AppShell>;

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-6 py-10" data-testid="creative-studio-page">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-300">Creative Studio</p>
            <h1 className="font-display text-4xl md:text-5xl text-white mt-2">Show off your creative side.</h1>
            <p className="text-slate-400 mt-2 max-w-xl">Submit a clip, script, drawing, or write-up. Get honest AI feedback on whether to pursue this path — and where to grow.</p>
          </div>
          <button data-testid="creative-new-submission" onClick={() => setShowForm(!showForm)} className="brimi-pill accent-coral flex items-center gap-2">
            <Upload className="w-4 h-4" /> New submission
          </button>
        </div>

        {showForm && (
          <form onSubmit={submit} className="mt-8 brimi-card p-6 space-y-5" data-testid="creative-form">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Creative field</label>
              <div className="mt-2 flex flex-wrap gap-2">
                {fields.map((f) => {
                  const Icon = ICONS[f.id] || Palette;
                  const active = fieldId === f.id;
                  return (
                    <button type="button" key={f.id} data-testid={`creative-field-${f.id}`} onClick={() => setFieldId(f.id)}
                      className={`px-3 py-2 rounded-full text-xs font-bold border transition flex items-center gap-1.5 ${
                        active ? "text-slate-900" : "border-slate-200 text-slate-700 hover:bg-slate-50"
                      }`}
                      style={active ? { background: "#FBBF24", borderColor: "#FBBF24" } : {}}>
                      <Icon className="w-3.5 h-3.5" /> {f.name}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Title (optional)</label>
              <input data-testid="creative-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g., My first short film" className="mt-2 w-full border border-slate-200 rounded-xl px-3 py-2.5 text-slate-900 outline-none focus:ring-2 focus:ring-blue-100" />
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Describe your work</label>
              <textarea data-testid="creative-writeup" value={writeup} onChange={(e) => setWriteup(e.target.value)} rows={6} required
                placeholder="What did you make? What was your intent? What are you unsure about? Include details — the more we know, the sharper the feedback."
                className="mt-2 w-full border border-slate-200 rounded-xl px-3 py-2.5 text-slate-900 outline-none focus:ring-2 focus:ring-blue-100" />
            </div>

            <div>
              <label className="text-xs uppercase tracking-[0.2em] text-slate-500 font-bold">Upload a file (optional · max 40 MB)</label>
              <div className="mt-2 flex items-center gap-3">
                <input data-testid="creative-file" type="file" accept="audio/*,video/*,image/*,application/pdf,text/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-slate-100 file:text-slate-800 file:font-bold" />
                {file && <button type="button" onClick={() => setFile(null)} className="text-xs text-slate-500 underline">Clear</button>}
              </div>
              <p className="text-xs text-slate-500 mt-1">Audio/video → transcribed with Whisper. JPEG/PNG/WEBP images (≤8 MB) → analyzed by GPT-5.2 vision. Other files → text-only critique.</p>
            </div>

            <div className="flex justify-end">
              <button data-testid="creative-submit-button" type="submit" disabled={busy} className="brimi-pill accent-coral flex items-center gap-2 disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {busy ? "Analyzing…" : "Get AI feedback"}
              </button>
            </div>
          </form>
        )}

        {selected && <FeedbackCard sub={selected} onClose={() => setSelected(null)} />}

        {/* Past submissions */}
        <div className="mt-10">
          <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-300 mb-3">Your past submissions</p>
          {submissions.length === 0 ? (
            <div className="brimi-card p-10 text-center text-slate-500">No submissions yet. Try one above!</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {submissions.map((s, i) => {
                const v = VERDICT_STYLE[s.feedback?.verdict] || VERDICT_STYLE["Explore More First"];
                return (
                  <button key={i} data-testid={`creative-sub-${i}`} onClick={() => setSelected(s)} className="brimi-card p-5 text-left">
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ background: v.bg, color: v.color }}>
                        {s.feedback?.verdict || "Reviewed"} · {s.feedback?.score ?? 0}
                      </span>
                      <span className="text-xs text-slate-500">{new Date(s.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="font-display text-lg text-slate-900 mt-2">{s.title || s.field_name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{s.field_name}</p>
                    <p className="text-sm text-slate-700 mt-2 line-clamp-2">{s.writeup || s.transcript || "—"}</p>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function FeedbackCard({ sub, onClose }) {
  const fb = sub.feedback || {};
  const v = VERDICT_STYLE[fb.verdict] || VERDICT_STYLE["Explore More First"];
  const VIcon = v.icon;
  return (
    <div className="mt-8 brimi-card p-8" data-testid="creative-feedback">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">AI critique · {sub.field_name}</p>
          <h2 className="font-display text-3xl text-slate-900 mt-1">{sub.title || "Your submission"}</h2>
        </div>
        <button onClick={onClose} className="text-xs font-bold text-slate-500 hover:text-slate-900">Close ✕</button>
      </div>

      <div className="mt-5 flex items-center gap-4 flex-wrap">
        <div className="px-4 py-2 rounded-full flex items-center gap-2 font-bold" style={{ background: v.bg, color: v.color }}>
          <VIcon className="w-4 h-4" /> {fb.verdict || "Reviewed"}
        </div>
        <div className="flex items-center gap-2">
          <div className="text-2xl font-display text-slate-900">{fb.score ?? 0}<span className="text-sm text-slate-500">/100</span></div>
          <div className="w-32 h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full" style={{ width: `${fb.score || 0}%`, background: "linear-gradient(90deg, #FB7185, #FBBF24, #34D399)" }} />
          </div>
        </div>
      </div>

      {fb.encouragement && (
        <p className="mt-5 p-4 rounded-xl bg-yellow-50 border border-yellow-200 text-slate-800">{fb.encouragement}</p>
      )}

      {sub.vision_used && (
        <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold" style={{ background: "#EDE9FE", color: "#5B21B6" }}>
          <Sparkles className="w-3.5 h-3.5" /> Vision-analyzed by GPT-5.2
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5 mt-6">
        <ListBox title="Strengths" items={fb.strengths} color="#34D399" />
        <ListBox title="Growth areas" items={fb.growth_areas} color="#FB7185" />
        <ListBox title="Next steps" items={fb.next_steps} color="#3B82F6" />
        <ListBox title="Adjacent fields to explore" items={fb.adjacent_fields} color="#FBBF24" />
      </div>

      {fb.learning_resources?.length > 0 && (
        <div className="mt-6 p-4 rounded-xl bg-slate-50 border border-slate-100">
          <p className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">Learning resources</p>
          <ul className="mt-2 list-disc list-inside text-slate-800 text-sm space-y-1">
            {fb.learning_resources.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}

      {sub.transcript && (
        <div className="mt-6 p-4 rounded-xl bg-slate-50 border border-slate-100">
          <p className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">Whisper transcript</p>
          <p className="text-sm text-slate-700 mt-1 whitespace-pre-wrap">{sub.transcript}</p>
        </div>
      )}
    </div>
  );
}

function ListBox({ title, items, color }) {
  return (
    <div className="p-4 rounded-xl border border-slate-100">
      <p className="text-[11px] uppercase tracking-[0.15em] font-bold" style={{ color }}>{title}</p>
      <ul className="mt-2 space-y-1 text-sm text-slate-800">
        {(items || []).map((x, i) => <li key={i} className="flex gap-2"><span className="text-slate-400">•</span> {x}</li>)}
        {(!items || items.length === 0) && <li className="text-slate-400">—</li>}
      </ul>
    </div>
  );
}
